import { query } from '../api/query'
import { getBookingApiRoot } from '../helpers/bookingApiRoutes'
import {
    applySummaryBookingDetails,
    applySummaryExtras,
    applySummaryPrice,
} from '../helpers/updateBookingSummary'

class BookingSummary {
    constructor(summaryElement, bookingNo, extras) {
        this.summaryElement = summaryElement
        this.bookingNo = bookingNo
        this.extras = extras
        this.bookingApiRoot = getBookingApiRoot()
        this.loadingSpinner = this.summaryElement?.querySelector('#loading-spinner-booking-summary')
        this.inputs = Array.from(this.summaryElement?.querySelectorAll('[data-stay-editor-input]') ?? [])
        this.updateRoute = this.bookingApiRoot?.dataset?.updateBookingAvailabilityRoute ?? ''
        this.availableStayLengthsRoute = this.bookingApiRoot?.dataset?.availableStayLengthsRoute ?? ''
        this.updateError = this.summaryElement?.querySelector('[data-booking-summary-update-error]')
        this.confirmedValues = Object.fromEntries(
            this.inputs.map((input) => [input.dataset.stayEditorInput, input.value])
        )
        this.isUpdating = false
        this.hasLoadedAvailableStayLengths = false

        if (this.summaryElement && this.inputs.length > 0 && this.updateRoute) {
            this.addEventListeners()
            this.deferAvailableStayLengths()
        }
    }

    deferAvailableStayLengths() {
        const load = () => this.loadAvailableStayLengths()

        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(load, { timeout: 2500 })
            return
        }

        window.setTimeout(load, 1000)
    }

    async loadAvailableStayLengths() {
        const nightsInput = this.inputs.find((input) => input.dataset.stayEditorInput === 'no_of_nights')

        if (!nightsInput || !this.availableStayLengthsRoute || this.hasLoadedAvailableStayLengths) {
            return
        }

        this.hasLoadedAvailableStayLengths = true
        const selectedNights = String(nightsInput.value)
        nightsInput.disabled = true

        try {
            const response = await query(this.availableStayLengthsRoute, this.bookingNo)
            const stayLengths = Array.isArray(response.stay_lengths) ? response.stay_lengths : []

            if (stayLengths.length === 0) {
                return
            }

            nightsInput.innerHTML = ''

            stayLengths.forEach((stayLength) => {
                const option = document.createElement('option')
                option.value = String(stayLength.no_of_nights)
                option.textContent = stayLength.label
                nightsInput.appendChild(option)
            })

            nightsInput.value = selectedNights
        } catch (error) {
            console.error('Failed to load available stay lengths:', error.message)
        } finally {
            nightsInput.disabled = false
        }
    }

    addEventListeners() {
        this.inputs.forEach((input) => {
            input.addEventListener('change', async () => {
                await this.updateStayDetails()
            })
        })
    }

    async updateStayDetails() {
        if (this.isUpdating) {
            return
        }

        const invalidInput = this.inputs.find((input) => !input.checkValidity())

        if (invalidInput) {
            invalidInput.reportValidity()
            return
        }

        this.isUpdating = true
        this.hideUpdateError()
        this.setDisabledState(true)
        this.toggleLoading(true)
        this.syncRootDataset()

        try {
            const response = await query(this.updateRoute, this.bookingNo)
            applySummaryBookingDetails(response.booking ?? {})
            applySummaryPrice(response.booking ?? {})
            applySummaryExtras(response.extras ?? [])
            this.syncRootDataset(response.booking ?? {})
            this.extras?.applyAvailabilityResponse(response)
            this.confirmedValues = Object.fromEntries(
                this.inputs.map((input) => [input.dataset.stayEditorInput, input.value])
            )
        } catch (error) {
            console.error('Failed to update booking summary:', error.message)
            this.inputs.forEach((input) => {
                input.value = this.confirmedValues[input.dataset.stayEditorInput] ?? input.value
            })
            this.syncRootDataset()
            this.showUpdateError()
        } finally {
            this.toggleLoading(false)
            this.setDisabledState(false)
            this.isUpdating = false
        }
    }

    hideUpdateError() {
        this.updateError?.classList.add('hidden')
    }

    showUpdateError() {
        this.updateError?.classList.remove('hidden')
    }

    syncRootDataset(booking = null) {
        if (!this.bookingApiRoot) {
            return
        }

        const values = booking ?? Object.fromEntries(
            this.inputs.map((input) => [input.dataset.stayEditorInput, input.value])
        )

        if (values.arrival_date) {
            this.bookingApiRoot.dataset.arrivalDate = values.arrival_date
        }

        if (values.no_of_nights !== undefined) {
            this.bookingApiRoot.dataset.noOfNights = String(values.no_of_nights)
        }

        if (values.no_of_adults !== undefined) {
            this.bookingApiRoot.dataset.noOfAdults = String(values.no_of_adults)
        }

        if (values.no_of_children !== undefined) {
            this.bookingApiRoot.dataset.noOfChildren = String(values.no_of_children)
        }

        if (values.no_of_pets !== undefined) {
            this.bookingApiRoot.dataset.noOfPets = String(values.no_of_pets)
        }
    }

    setDisabledState(disabled) {
        this.inputs.forEach((input) => {
            input.disabled = disabled
        })
    }

    toggleLoading(show) {
        if (!this.loadingSpinner) {
            return
        }

        this.loadingSpinner.classList.toggle('hidden', !show)
    }
}

export default BookingSummary
